import { useEffect, useReducer } from "react"
import "../../filterSection.scss"
import { t } from "@lingui/core/macro"
import { UrlState } from "@multimediallc/cb-roomlist-prefetch"
import {
    addOrientationChangeListener,
    removeOrientationChangeListener,
} from "@multimediallc/web-utils"
import { useUrlStateContext } from "../../../../../../../hooks/urlStateContext"
import { Chevron } from "../../../../../../common/atoms/Icons/Others"
import { IconButton } from "../../../../../../messaging/common/IconButton/IconButton"
import { useFiltersContext } from "../../../FiltersContext"
import { FilterSection } from "../../FilterSection"
import { HashtagSearch } from "../HashtagSearch"
import { useTagPagination } from "./useTagPagination"
import "./TagPagination.scss"

export const HashtagSection = () => {
    const { setTagSearchInput } = useFiltersContext()

    const urlStateContext = useUrlStateContext()
    const selectedHashtag = urlStateContext.tags?.[0] ?? ""
    const clearSelections = () => {
        if (selectedHashtag === "") {
            // button should have no effect when no tag is selected
            return
        }
        UrlState.current.setPartialState({ tags: undefined })
        setTagSearchInput("")
    }

    return (
        <FilterSection
            isClearDisabled={selectedHashtag === ""}
            title="Tags"
            onClear={clearSelections}
            clearButtonText="Clear"
        >
            <HashtagSectionContent />
        </FilterSection>
    )
}

function renderTag(
    tag: string,
    registerRef: (el: HTMLButtonElement | null) => void,
): React.ReactNode {
    return (
        <button
            key={tag}
            ref={registerRef}
            className="FilterSection__filterOption"
        >
            {tag}
        </button>
    )
}

export const HashtagSectionContent = ({
    combinedOverlay = false,
}: {
    combinedOverlay?: boolean | undefined
}) => {
    const { topTags, errorTopTags } = useFiltersContext()
    const urlStateContext = useUrlStateContext()
    const [, forceUpdate] = useReducer((x) => (x ?? 0) + 1, 0)
    const selectedHashtag: string = urlStateContext.tags?.[0] ?? ""

    useEffect(() => {
        if (!(screen?.orientation && screen.orientation?.type)) {
            return
        }

        const handleOrientationChange = () => {
            forceUpdate()
        }
        addOrientationChangeListener(handleOrientationChange)
        return () => {
            removeOrientationChangeListener(handleOrientationChange)
        }
    }, [])

    const {
        currentPage,
        totalPages,
        currentTags,
        nextPage,
        prevPage,
        toFirstPage,
        renderMeasurementDiv,
    } = useTagPagination({
        tags: topTags,
        combinedOverlay,
    })

    const currentTagsWithSelected =
        selectedHashtag === ""
            ? [...currentTags]
            : currentTags.includes(selectedHashtag)
              ? [
                    selectedHashtag,
                    ...currentTags.filter((tag) => tag !== selectedHashtag),
                ]
              : [selectedHashtag, ...currentTags]

    return (
        <>
            {totalPages > 1 && <HashtagSearch />}
            {renderMeasurementDiv(renderTag)}
            <div
                className={`FilterSection__filterOptions${combinedOverlay ? "Combined" : ""} Hashtags`}
            >
                {errorTopTags ? (
                    <div
                        className="filterOptionsStatus--Error"
                        data-testid="top-tags-load-failed"
                    >
                        {t`Failed to load top tags`}
                    </div>
                ) : topTags.length === 0 ? (
                    <div className="filterOptionsStatus--Empty">
                        {t`No rooms with hashtags match your filters.`}
                    </div>
                ) : (
                    currentTagsWithSelected.map((hashtag) => (
                        <button
                            key={hashtag}
                            className={`FilterSection__filterOption ${
                                selectedHashtag === hashtag ? "active" : ""
                            }`}
                            onClick={() => {
                                toFirstPage()
                                UrlState.current.setPartialState({
                                    tags:
                                        hashtag === selectedHashtag
                                            ? undefined
                                            : [hashtag],
                                })
                            }}
                            data-testid={`hashtag-button-${hashtag}`}
                        >
                            {hashtag}
                        </button>
                    ))
                )}
            </div>
            {totalPages > 1 && (
                <div
                    className={`TagPagination${combinedOverlay ? " Combined" : ""}`}
                >
                    <div className="TagPagination__controls">
                        <IconButton
                            className="TagPagination__prev-icon"
                            icon={<Chevron height={15} />}
                            onClick={prevPage}
                            aria-label={t`Previous page`}
                        />
                        <span className="TagPagination__page-info">{t`${currentPage} of ${totalPages}`}</span>
                        <div
                            className="TagPagination__next-div"
                            onClick={nextPage}
                        >
                            <span className="TagPagination__next-text">{t`next`}</span>
                            <IconButton
                                className="TagPagination__next-icon"
                                icon={<Chevron height={15} />}
                                aria-label={t`Next page`}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
